import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLang, useTextDomainContext, useUser } from "@/app/providers";
import {
  GENERATE_ENERGY_STATEMENT_FILE,
  useClimateData,
  useGetGroups,
} from "@/hooks";
import {
  FilterBar1,
  AreaChartBox,
  LineChartBox,
  BarChartBox,
  TableView1,
  ToolbarDropdown,
} from "@/components";
import moment from "moment";
import {
  exportableStatements,
  formatDateForAPI,
  formatDateYear,
  getPreviousYearDate,
  segmentList,
  toBenchmarkDataClimate,
  toLocaleNumber,
} from "@/utils";
import { useTheme, Box, Typography } from "@mui/material";
import type { GridRowClassNameParams } from "@mui/x-data-grid";
import { TfiImport } from "react-icons/tfi";
import { enqueueSnackbar } from "notistack";
import { useLazyQuery, useQuery } from "@apollo/client/react";
import { queries } from "@/graphql";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FunctionsIcon from "@mui/icons-material/Functions";

export const Carbon = () => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);

  // Fetch climate-chart-type settings with user -> tenant -> global fallback
  const { data: userSettingData } = useQuery(queries.userSettings, {
    variables: { key: "climate-chart-type" },
  });

  const { data: tenantSettingData } = useQuery(queries.tenantSettings, {
    variables: { key: "climate-chart-type" },
  });

  const { data: globalSettingData } = useQuery(queries.getSettings, {
    variables: { key: "climate-chart-type" },
  });

  const activeChartType = useMemo(() => {
    const extractVal = (raw: any): string | null => {
      if (!raw) return null;
      let parsed: any = raw;
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "userSettings" in parsed
      ) {
        parsed = parsed.userSettings;
      }
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "userSettings" in parsed
      ) {
        parsed = parsed.userSettings;
      }
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "tenantSettings" in parsed
      ) {
        parsed = parsed.tenantSettings;
      }
      if (
        typeof parsed === "object" &&
        parsed !== null &&
        "settings" in parsed
      ) {
        parsed = parsed.settings;
      }
      return typeof parsed === "string" ? parsed : null;
    };

    // Priority 1: User Setting
    const userVal = extractVal(userSettingData);
    if (userVal && userVal !== "Default") {
      return userVal;
    }

    // Priority 2: Tenant Setting
    const tenantVal = extractVal(tenantSettingData);
    if (tenantVal && tenantVal !== "Default") {
      return tenantVal;
    }

    // Priority 3: System (Global) Setting
    const globalVal = extractVal(globalSettingData);
    if (globalVal && globalVal !== "Default") {
      return globalVal;
    }

    return "Bar Chart";
  }, [userSettingData, tenantSettingData, globalSettingData]);

  const SHOW_AREA_CHART = activeChartType === "Area Chart";
  const SHOW_LINE_CHART = activeChartType === "Line Chart";
  const SHOW_BAR_CHART = activeChartType === "Bar Chart";
  const { user } = useUser();
  const isSuperAdmin = user?.roles?.includes("super-admin");
  const segments = segmentList();
  let {
    currentTeam: customer,
    currentSubsidiary: group,
    currentTeamId,
    currentSubsidiaryId,
  } = user || {};
  const locale = useLang();
  const theme = useTheme();
  if (!customer) {
    customer = "all";
  }
  if (!group) {
    group = "All";
  }

  //region React Hooks (useState)
  const [building, setBuilding] = useState("all");
  const [meter, setMeter] = useState("all");
  const [energyType, setEnergyType] = useState("electricity");
  const [fromDate, setFromDate] = useState(() => {
    const date = new Date();
    date.setMonth(0, 1); // January 1st of current year
    date.setHours(0, 0, 0, 0);
    return date.toISOString();
  });
  const [toDate, setToDate] = useState(() => {
    const date = new Date();
    date.setHours(23, 59, 59, 999);
    return date.toISOString();
  });
  // const [segment, setSegment] = useState("year");
  const [shortSegment, setShortSegment] = useState("m");
  // const [chartData, setChartData] = useState([]);
  // const [fileNameForExport, setFileNameForExport] = useState(
  //   "carbon-emissions-export",
  // );
  // const [tableData, setTableData] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [country, setCountry] = useState(null);
  //endregion  React Hooks (useState)

  //region Data
  // const filters = {
  //   customer,
  //   group,
  //   address: building,
  //   meteringPointId: meter,
  //   meterType: energyType,
  //   fromDate: formatDateForAPI(fromDate),
  //   toDate: formatDateForAPI(toDate),
  //   resolution: shortSegment,
  //   fileType: "csv",
  // };
  // const { data: metersData, loading } = useClimateData(filters);

  const thisYearFilter = {
    customer,
    group,
    address: building,
    meteringPointId: meter,
    meterType: energyType,
    fromDate: formatDateForAPI(fromDate),
    toDate: formatDateForAPI(toDate),
    resolution: shortSegment,
    country,
  };

  const previousYearFilter = {
    customer,
    group,
    address: building,
    meteringPointId: meter,
    meterType: energyType,
    fromDate: getPreviousYearDate(thisYearFilter.fromDate),
    toDate: getPreviousYearDate(thisYearFilter.toDate),
    resolution: shortSegment,
    country,
  };

  const twoYearsAgoFilter = {
    customer,
    group,
    address: building,
    meteringPointId: meter,
    meterType: energyType,
    fromDate: getPreviousYearDate(previousYearFilter.fromDate),
    toDate: getPreviousYearDate(previousYearFilter.toDate),
    resolution: shortSegment,
    country,
  };

  const {
    data: metersDataThisYear,
    total: totalThisYear,
    loading: loadingThisYear,
  } = useClimateData(thisYearFilter);

  const {
    data: metersDataPreviousYear,
    total: totalPreviousYear,
    loading: loadingPreviousYear,
  } = useClimateData(previousYearFilter);

  const {
    data: metersDataTwoYearsAgo,
    total: totalTwoYearsAgo,
    loading: loadingTwoYearsAgo,
  } = useClimateData(twoYearsAgoFilter);

  const [addExcelInQueue] = useLazyQuery(GENERATE_ENERGY_STATEMENT_FILE);

  const { subsidiary } = useGetGroups({
    customerId: currentTeamId,
    customerName: customer,
  });

  const currentYearNum = new Date().getFullYear();
  const thisYear =
    formatDateYear(thisYearFilter.fromDate) || String(currentYearNum);
  const previousYear =
    formatDateYear(previousYearFilter.fromDate) || String(currentYearNum - 1);
  const twoYearsAgo =
    formatDateYear(twoYearsAgoFilter.fromDate) || String(currentYearNum - 2);
  //endregion Data

  //region React Hooks (useEffect)

  const tableData = useMemo(() => {
    return toBenchmarkDataClimate(
      shortSegment,
      [
        {
          data: metersDataThisYear,
          total: totalThisYear,
        },
        {
          data: metersDataPreviousYear,
          total: totalPreviousYear,
        },
        {
          data: metersDataTwoYearsAgo,
          total: totalTwoYearsAgo,
        },
      ],
      gettext,
      true,
    );
  }, [
    shortSegment,
    metersDataThisYear,
    totalThisYear,
    metersDataPreviousYear,
    totalPreviousYear,
    metersDataTwoYearsAgo,
    totalTwoYearsAgo,
    gettext,
  ]);

  const chartData = useMemo(() => {
    return toBenchmarkDataClimate(
      shortSegment,
      [
        {
          data: metersDataThisYear,
          total: totalThisYear,
        },
        {
          data: metersDataPreviousYear,
          total: totalPreviousYear,
        },
        {
          data: metersDataTwoYearsAgo,
          total: totalTwoYearsAgo,
        },
      ],
      gettext,
      false,
    );
  }, [
    shortSegment,
    metersDataThisYear,
    totalThisYear,
    metersDataPreviousYear,
    totalPreviousYear,
    metersDataTwoYearsAgo,
    totalTwoYearsAgo,
  ]);

  // useEffect(() => {
  //   const calculatedTableData = toBenchmarkDataClimate(
  //     shortSegment,
  //     [
  //       {
  //         data: metersDataThisYear,
  //         total: totalThisYear,
  //       },
  //       {
  //         data: metersDataPreviousYear,
  //         total: totalPreviousYear,
  //       },
  //     ],
  //     gettext,
  //     true,
  //   );
  //
  //   const calculatedChartData = toBenchmarkDataClimate(
  //     shortSegment,
  //     [
  //       {
  //         data: metersDataThisYear,
  //         total: totalThisYear,
  //       },
  //       {
  //         data: metersDataPreviousYear,
  //         total: totalPreviousYear,
  //       },
  //     ],
  //     false,
  //   );
  //
  //   if (!isEqual(tableData, calculatedTableData)) {
  //     setTableData(calculatedTableData);
  //   }
  //
  //   if (!isEqual(chartData, calculatedChartData)) {
  //     setChartData(calculatedChartData);
  //   }
  //
  //   // console.log(calculatedTableData);
  // }, [
  //   chartData,
  //   gettext,
  //   metersDataPreviousYear,
  //   metersDataThisYear,
  //   shortSegment,
  //   tableData,
  //   totalPreviousYear,
  //   totalThisYear,
  // ]);

  // useEffect(() => {
  //   setFileNameForExport(
  //     urlFriendlyString(
  //       "carbon-emissions-" +
  //         building +
  //         "-" +
  //         meter +
  //         "-" +
  //         energyType +
  //         "-" +
  //         segment +
  //         "-" +
  //         fromDate +
  //         "-" +
  //         toDate,
  //     ),
  //   );
  // }, [building, energyType, fromDate, meter, segment, toDate]);

  useEffect(() => {
    const fetchData = async () => {
      const customerName = group.toLowerCase() === "all" ? customer : group;
      let fileNamePostfix = group.toLowerCase() === "all" ? "-all" : "";
      if (subsidiary.length < 2) {
        fileNamePostfix = "-all";
      }
      const res = [];

      const res1 = await exportableStatements(
        "climate-statement",
        customerName,
        fileNamePostfix,
        energyType,
        fromDate,
        gettext,
      );

      if (isSuperAdmin) {
        // const res2 = await exportableStatementsTest(
        //   "climate-statement",
        //   customerName,
        //   fileNamePostfix,
        //   energyType,
        //   fromDate,
        //   gettext,
        // );

        // res.push(...res1, ...res2);
        res.push(...res1);

        const obj = {
          label: gettext("Generate Climate Report"),
          key: "generate-report",
        };

        res.push(obj);
      } else {
        res.push(...res1);
      }

      setExportData(res);
    };
    fetchData().then();
  }, [
    customer,
    energyType,
    fromDate,
    gettext,
    group,
    isSuperAdmin,
    subsidiary.length,
  ]);

  // useEffect(() => {
  //   if (metersData) {
  //     const chartDateTemp = meterApiDataToBarChartData(
  //       metersData,
  //       shortSegment,
  //     );
  //     if (!isEqual(chartDateTemp, chartData)) {
  //       setChartData(meterApiDataToBarChartData(metersData, shortSegment));
  //     }
  //   }
  // }, [chartData, metersData, shortSegment]);

  //endregion React Hooks (useEffect)

  //region For Table

  const dateColumn = {
    field: "ts",
    headerName: gettext("Date"),
    flex: 1,
    headerAlign: "center",
    renderCell: (params: any) => {
      if (params.row.id === "Total") {
        return (
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              px: 1.2,
              py: 0.4,
              borderRadius: "6px",
              backgroundColor: "primary.main",
              color: "primary.contrastText",
              fontWeight: 700,
              fontSize: "0.75rem",
              letterSpacing: "0.5px",
              boxShadow: "0 1px 3px rgba(0,0,0,0.15)",
            }}
          >
            <FunctionsIcon sx={{ fontSize: "15px" }} />
            <span>{params.value || gettext("SUM")}</span>
          </Box>
        );
      }
      return <span style={{ fontWeight: 500 }}>{params.value}</span>;
    },
  };

  const renderComparisonCell =
    (prevField?: string, isRate = false, unitLabel = "") =>
    (params: any) => {
      const isTotalRow = params.row.id === "Total";
      const val = params.value;

      if (val === "" || (isRate && val === null)) {
        return "";
      }

      const formattedValue = toLocaleNumber(locale, val ?? 0, 2);

      if (val === undefined || val === null) {
        return formattedValue;
      }

      let prevVal: any = undefined;
      if (prevField) {
        prevVal = params.row[prevField];
        if (prevVal === undefined || prevVal === null) {
          const match = prevField.match(/^([a-zA-Z_]+)?(\d{4})$/);
          if (match) {
            const prefix = match[1] || "";
            const year = Number(match[2]);
            const fallbackField = `${prefix}${year - 1}`;
            if (
              params.row[fallbackField] !== undefined &&
              params.row[fallbackField] !== null
            ) {
              prevVal = params.row[fallbackField];
            }
          }
        }
      }

      let diffInfo: {
        diffStr: string;
        pctStr: string | null;
        isHigher: boolean;
        isLower: boolean;
        color: string;
        bgColor: string;
      } | null = null;

      if (
        prevField &&
        prevVal !== undefined &&
        prevVal !== null &&
        prevVal !== ""
      ) {
        const numericVal = Number(val);
        const numericPrevVal = Number(prevVal);

        if (!isNaN(numericVal) && !isNaN(numericPrevVal)) {
          const diff = numericVal - numericPrevVal;
          // Percentage calculation without Abs function!
          const pct =
            numericPrevVal !== 0
              ? (diff / Math.abs(numericPrevVal)) * 100
              : null;

          const formattedDiff = `${diff > 0 ? "+" : ""}${toLocaleNumber(locale, diff, 2)}`;
          const formattedPct =
            pct !== null
              ? `${pct > 0 ? "+" : ""}${toLocaleNumber(locale, pct, 1)}%`
              : null;

          const isHigher = diff > 0;
          const isLower = diff < 0;

          diffInfo = {
            diffStr: formattedDiff,
            pctStr: formattedPct,
            isHigher,
            isLower,
            color: isHigher
              ? theme.palette.error.main
              : isLower
                ? theme.palette.success.main
                : theme.palette.text.secondary,
            bgColor: isHigher
              ? theme.palette.mode === "dark"
                ? "rgba(244, 67, 54, 0.15)"
                : "#ffebee"
              : isLower
                ? theme.palette.mode === "dark"
                  ? "rgba(76, 175, 80, 0.15)"
                  : "#e8f5e9"
                : theme.palette.mode === "dark"
                  ? "rgba(255, 255, 255, 0.08)"
                  : "#f5f5f5",
          };
        }
      }

      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "flex-end",
            width: "100%",
            py: 0.5,
            px: 0.5,
            gap: 0.25,
          }}
        >
          {/* Main Value */}
          <Box
            sx={{
              fontWeight: isTotalRow ? 700 : 600,
              fontSize: isTotalRow ? "0.9rem" : "0.85rem",
              color: isTotalRow ? "primary.main" : "text.primary",
              lineHeight: 1.2,
            }}
          >
            {formattedValue}{" "}
            {unitLabel && (
              <Typography
                component="span"
                sx={{
                  fontSize: "0.72rem",
                  fontWeight: isTotalRow ? 600 : 500,
                  color: isTotalRow ? "primary.main" : "text.secondary",
                }}
              >
                {unitLabel}
              </Typography>
            )}
          </Box>

          {/* Difference & Percentage Badge */}
          {diffInfo && (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: "3px",
                color: diffInfo.color,
                backgroundColor: diffInfo.bgColor,
                fontWeight: isTotalRow ? 700 : 600,
                fontSize: "0.72rem",
                mt: 0.25,
                px: 0.75,
                py: 0.2,
                borderRadius: "4px",
                border: isTotalRow ? `1px solid ${diffInfo.color}` : "none",
              }}
            >
              {diffInfo.isHigher && (
                <ArrowUpwardIcon sx={{ fontSize: "13px" }} />
              )}
              {diffInfo.isLower && (
                <ArrowDownwardIcon sx={{ fontSize: "13px" }} />
              )}
              <span>
                {diffInfo.diffStr} {unitLabel}
              </span>
              {diffInfo.pctStr && <span>({diffInfo.pctStr})</span>}
            </Box>
          )}
        </Box>
      );
    };

  const columnGroupingModelConsumption = [
    {
      groupId: gettext("Consumption (kWh)"),
      headerAlign: "center",
      headerClassName: "borderLeft",
      children: [
        { field: `consumptionV_${twoYearsAgo}` },
        { field: `consumptionV_${previousYear}` },
        { field: `consumptionV_${thisYear}` },
      ],
    },
  ];

  const columnsConsumption = [
    dateColumn,
    {
      field: `consumptionV_${twoYearsAgo}`,
      headerName: twoYearsAgo,
      flex: 1.2,
      type: "number",
      headerAlign: "center",
      headerClassName: "borderLeft",
      renderCell: renderComparisonCell(undefined, false, "kWh"),
    },
    {
      field: `consumptionV_${previousYear}`,
      headerName: previousYear,
      flex: 1.5,
      type: "number",
      headerAlign: "center",
      renderCell: renderComparisonCell(
        `consumptionV_${twoYearsAgo}`,
        false,
        "kWh",
      ),
    },
    {
      field: `consumptionV_${thisYear}`,
      headerName: thisYear,
      flex: 1.5,
      type: "number",
      headerAlign: "center",
      renderCell: renderComparisonCell(
        `consumptionV_${previousYear}`,
        false,
        "kWh",
      ),
    },
  ];

  const columnGroupingModelCarbonFootprint = [
    {
      groupId: gettext("Carbon Footprint (g CO₂e/kWh)"),
      headerAlign: "center",
      headerClassName: "borderLeft",
      children: [
        { field: `conversionRate_${twoYearsAgo}` },
        { field: `conversionRate_${previousYear}` },
        { field: `conversionRate_${thisYear}` },
      ],
    },
  ];

  const columnsCarbonFootprint = [
    dateColumn,
    {
      field: `conversionRate_${twoYearsAgo}`,
      headerName: twoYearsAgo,
      flex: 1.2,
      type: "number",
      headerAlign: "center",
      headerClassName: "borderLeft",
      renderCell: renderComparisonCell(undefined, true, "g CO₂e/kWh"),
    },
    {
      field: `conversionRate_${previousYear}`,
      headerName: previousYear,
      flex: 1.5,
      type: "number",
      headerAlign: "center",
      renderCell: renderComparisonCell(
        `conversionRate_${twoYearsAgo}`,
        true,
        "g CO₂e/kWh",
      ),
    },
    {
      field: `conversionRate_${thisYear}`,
      headerName: thisYear,
      flex: 1.5,
      type: "number",
      headerAlign: "center",
      renderCell: renderComparisonCell(
        `conversionRate_${previousYear}`,
        true,
        "g CO₂e/kWh",
      ),
    },
  ];

  const columnGroupingModelCarbonEmissions = [
    {
      groupId: gettext("Carbon Emissions (Kg CO₂e)"),
      headerAlign: "center",
      headerClassName: "borderLeft",
      children: [
        { field: `co2_${twoYearsAgo}` },
        { field: `co2_${previousYear}` },
        { field: `co2_${thisYear}` },
      ],
    },
  ];

  const columnsCarbonEmissions = [
    dateColumn,
    {
      field: `co2_${twoYearsAgo}`,
      headerName: twoYearsAgo,
      flex: 1.2,
      type: "number",
      headerAlign: "center",
      headerClassName: "borderLeft",
      renderCell: renderComparisonCell(undefined, false, "Kg CO₂e"),
    },
    {
      field: `co2_${previousYear}`,
      headerName: previousYear,
      flex: 1.5,
      type: "number",
      headerAlign: "center",
      renderCell: renderComparisonCell(`co2_${twoYearsAgo}`, false, "Kg CO₂e"),
    },
    {
      field: `co2_${thisYear}`,
      headerName: thisYear,
      flex: 1.5,
      type: "number",
      headerAlign: "center",
      renderCell: renderComparisonCell(`co2_${previousYear}`, false, "Kg CO₂e"),
    },
  ];

  const initialState = {
    pagination: {
      paginationModel: {
        page: 0,
        pageSize: 100,
      },
    },
    // columns: {
    //   columnVisibilityModel: {
    //     id: false,
    //   }
    // }
  };

  const postToolbar = () => {
    return (
      <ToolbarDropdown
        data={exportData}
        label={gettext("Reports")}
        onClick={handleClickCustomDropdownOption}
        endIcon={<TfiImport size={14} />}
      />
    );
  };
  //endregion For Table

  const handleGetSelectedValues = (selectedValues: {
    building?: { value?: string };
    meter?: { value?: string };
    energyType?: { value?: string };
    date?: { startDate?: Date; endDate?: Date };
    segment?: keyof typeof segments;
    country?: string | null;
  }) => {
    if (selectedValues?.building?.value) {
      setBuilding(selectedValues.building.value);
    }

    if (selectedValues?.meter?.value) {
      setMeter(selectedValues.meter.value);
    }

    if (selectedValues?.energyType?.value) {
      setEnergyType(selectedValues.energyType.value);
    }

    if (selectedValues?.date?.startDate) {
      setFromDate(selectedValues.date.startDate.toISOString());
    }

    if (selectedValues?.date?.endDate) {
      setToDate(selectedValues.date.endDate.toISOString());
    }

    if (selectedValues?.segment) {
      // setSegment(selectedValues.segment);
      setShortSegment(segments[selectedValues.segment].shortSegment);
      if (
        selectedValues.segment === "day" &&
        moment(selectedValues?.date?.startDate).format("YYYY-MM-DD") !==
          moment(selectedValues?.date?.endDate).format("YYYY-MM-DD")
      ) {
        // setSegment("month");
        setShortSegment(segments["month"].shortSegment);
      }
    }

    if (selectedValues?.country) {
      setCountry(selectedValues.country);
    }
  };

  const handleClickCustomDropdownOption = (obj: {
    key: string;
    label?: string;
    url?: string;
  }) => {
    let renamedGroup = group;
    if (subsidiary.length < 2) {
      renamedGroup = "all";
    }
    switch (obj.key) {
      case "generate-report":
        addExcelInQueue({
          variables: {
            meterType: energyType,
            customer: customer,
            group: renamedGroup,
            year: new Date(fromDate).getFullYear(),
            isClimate: true,
          },
        }).then(({ data }: any) => {
          if (data.generateEnergyStatementFile) {
            enqueueSnackbar(
              gettext(
                "The system is now creating the report, and you can download it shortly by clicking Download.",
              ),
              { variant: "success" },
            );
          } else {
            enqueueSnackbar(
              gettext(
                "An error occurred while the system was generating the report. Please try again. You can contact support If this continues.",
              ),
              { variant: "error" },
            );
          }
        });
        break;
      default:
        break;
    }
  };

  return (
    <>
      <FilterBar1
        getSelectedValues={handleGetSelectedValues}
        currentTeam={customer}
        currentSubsidiary={group}
        currentTeamId={currentTeamId}
        currentSubsidiaryId={currentSubsidiaryId}
        showCountry
        showBuilding
        showMeter
        showEnergyType
        showDateSingle
        showDateSingleHourly
        showDateSingleDaily
        showDateSingleMonthly
        // showDateMultiple
        showBuildingAll
        showMeterAll
        // showEnergyTypeAll
      />
      <br />

      {/*<BarChartBox*/}
      {/*  loading={loadingThisYear || loadingPreviousYear}*/}
      {/*  title={gettext("Kg of CO₂e")}*/}
      {/*  chartId={"co2"}*/}
      {/*  data={{*/}
      {/*    dataset: chartData,*/}
      {/*    series: [*/}
      {/*      {*/}
      {/*        dataKey: "co2",*/}
      {/*        label: gettext("Consumption"),*/}
      {/*        color: (theme.palette as any).chart.electricity,*/}
      {/*        unit: gettext("Kilogram"),*/}
      {/*      },*/}
      {/*    ],*/}
      {/*  }}*/}
      {/*/>*/}

      {SHOW_AREA_CHART && (
        <>
          <AreaChartBox
            loading={
              loadingThisYear || loadingPreviousYear || loadingTwoYearsAgo
            }
            title={gettext("Carbon Emissions (Kg CO₂e)")}
            chartId={"co2"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: `co2_${twoYearsAgo}`,
                  label: twoYearsAgo.toString(),
                  color: (theme.palette as any).chart.year3 || "#4472c4",
                  unit: gettext("Kg CO₂e"),
                },
                {
                  dataKey: `co2_${previousYear}`,
                  label: previousYear.toString(),
                  color: (theme.palette as any).chart.year2,
                  unit: gettext("Kg CO₂e"),
                },
                {
                  dataKey: `co2_${thisYear}`,
                  label: thisYear.toString(),
                  color: (theme.palette as any).chart.year1,
                  unit: gettext("Kg CO₂e"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_LINE_CHART && (
        <>
          <LineChartBox
            loading={
              loadingThisYear || loadingPreviousYear || loadingTwoYearsAgo
            }
            title={gettext("Carbon Emissions (Kg CO₂e)")}
            chartId={"co2-line"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: `co2_${twoYearsAgo}`,
                  label: twoYearsAgo.toString(),
                  color: (theme.palette as any).chart.year3 || "#4472c4",
                  unit: gettext("Kg CO₂e"),
                },
                {
                  dataKey: `co2_${previousYear}`,
                  label: previousYear.toString(),
                  color: (theme.palette as any).chart.year2,
                  unit: gettext("Kg CO₂e"),
                },
                {
                  dataKey: `co2_${thisYear}`,
                  label: thisYear.toString(),
                  color: (theme.palette as any).chart.year1,
                  unit: gettext("Kg CO₂e"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_BAR_CHART && (
        <>
          <BarChartBox
            loading={
              loadingThisYear || loadingPreviousYear || loadingTwoYearsAgo
            }
            title={gettext("Carbon Emissions (Kg CO₂e)")}
            chartId={"co2-bar"}
            data={{
              dataset: chartData as any,
              series: [
                {
                  dataKey: `co2_${twoYearsAgo}`,
                  label: twoYearsAgo.toString(),
                  color: (theme.palette as any).chart.year3 || "#4472c4",
                  unit: gettext("Kg CO₂e"),
                },
                {
                  dataKey: `co2_${previousYear}`,
                  label: previousYear.toString(),
                  color: (theme.palette as any).chart.year2,
                  unit: gettext("Kg CO₂e"),
                },
                {
                  dataKey: `co2_${thisYear}`,
                  label: thisYear.toString(),
                  color: (theme.palette as any).chart.year1,
                  unit: gettext("Kg CO₂e"),
                },
              ],
            }}
          />
          <br />
        </>
      )}
      <TableView1
        title={gettext("Carbon Emissions")}
        rows={tableData}
        columns={columnsCarbonEmissions}
        getRowHeight={() => "auto"}
        hideFooterPagination
        loading={loadingThisYear || loadingPreviousYear || loadingTwoYearsAgo}
        initialState={initialState}
        postToolbar={postToolbar()}
        showGridToolbar
        disableColumnSorting
        disableColumnSelector
        columnGroupingModel={columnGroupingModelCarbonEmissions}
        getRowClassName={(params: GridRowClassNameParams) => {
          return params.row.id === "Total" ? "total-row" : "";
        }}
      />
      <br />

      {/*<BarChartBox*/}
      {/*  loading={loadingThisYear || loadingPreviousYear}*/}
      {/*  title={gettext("Conversion rate in g CO₂e/kWh")}*/}
      {/*  chartId={"conversion"}*/}
      {/*  data={{*/}
      {/*    dataset: chartData,*/}
      {/*    series: [*/}
      {/*      {*/}
      {/*        dataKey: "conversionRate",*/}
      {/*        label: gettext("Consumption"),*/}
      {/*        color: (theme.palette as any).chart.electricity,*/}
      {/*        unit: gettext("g CO₂/kWh"),*/}
      {/*      },*/}
      {/*    ],*/}
      {/*  }}*/}
      {/*/>*/}

      {SHOW_AREA_CHART && (
        <>
          <AreaChartBox
            loading={
              loadingThisYear || loadingPreviousYear || loadingTwoYearsAgo
            }
            title={gettext("Carbon Footprint (g CO₂e/kWh)")}
            chartId={"conversion"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: `conversionRate_${twoYearsAgo}`,
                  label: twoYearsAgo.toString(),
                  color: (theme.palette as any).chart.year3 || "#4472c4",
                  unit: gettext("g CO₂e/kWh"),
                },
                {
                  dataKey: `conversionRate_${previousYear}`,
                  label: previousYear.toString(),
                  color: (theme.palette as any).chart.year2,
                  unit: gettext("g CO₂e/kWh"),
                },
                {
                  dataKey: `conversionRate_${thisYear}`,
                  label: thisYear.toString(),
                  color: (theme.palette as any).chart.year1,
                  unit: gettext("g CO₂e/kWh"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_LINE_CHART && (
        <>
          <LineChartBox
            loading={
              loadingThisYear || loadingPreviousYear || loadingTwoYearsAgo
            }
            title={gettext("Carbon Footprint (g CO₂e/kWh)")}
            chartId={"conversion-line"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: `conversionRate_${twoYearsAgo}`,
                  label: twoYearsAgo.toString(),
                  color: (theme.palette as any).chart.year3 || "#4472c4",
                  unit: gettext("g CO₂e/kWh"),
                },
                {
                  dataKey: `conversionRate_${previousYear}`,
                  label: previousYear.toString(),
                  color: (theme.palette as any).chart.year2,
                  unit: gettext("g CO₂e/kWh"),
                },
                {
                  dataKey: `conversionRate_${thisYear}`,
                  label: thisYear.toString(),
                  color: (theme.palette as any).chart.year1,
                  unit: gettext("g CO₂e/kWh"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_BAR_CHART && (
        <>
          <BarChartBox
            loading={
              loadingThisYear || loadingPreviousYear || loadingTwoYearsAgo
            }
            title={gettext("Carbon Footprint (g CO₂e/kWh)")}
            chartId={"conversion-bar"}
            data={{
              dataset: chartData as any,
              series: [
                {
                  dataKey: `conversionRate_${twoYearsAgo}`,
                  label: twoYearsAgo.toString(),
                  color: (theme.palette as any).chart.year3 || "#4472c4",
                  unit: gettext("g CO₂e/kWh"),
                },
                {
                  dataKey: `conversionRate_${previousYear}`,
                  label: previousYear.toString(),
                  color: (theme.palette as any).chart.year2,
                  unit: gettext("g CO₂e/kWh"),
                },
                {
                  dataKey: `conversionRate_${thisYear}`,
                  label: thisYear.toString(),
                  color: (theme.palette as any).chart.year1,
                  unit: gettext("g CO₂e/kWh"),
                },
              ],
            }}
          />
          <br />
        </>
      )}
      <TableView1
        title={gettext("Carbon Footprint")}
        rows={tableData.filter((row: any) => row.id !== "Total")}
        columns={columnsCarbonFootprint}
        getRowHeight={() => "auto"}
        hideFooterPagination
        loading={loadingThisYear || loadingPreviousYear || loadingTwoYearsAgo}
        initialState={initialState}
        postToolbar={postToolbar()}
        showGridToolbar
        disableColumnSorting
        disableColumnSelector
        columnGroupingModel={columnGroupingModelCarbonFootprint}
        getRowClassName={(params: GridRowClassNameParams) => {
          return params.row.id === "Total" ? "total-row" : "";
        }}
      />
      <br />

      {/*<BarChartBox*/}
      {/*  loading={loadingThisYear || loadingPreviousYear}*/}
      {/*  title={gettext("Consumption measured in kWh")}*/}
      {/*  chartId={"consumption"}*/}
      {/*  data={{*/}
      {/*    dataset: chartData,*/}
      {/*    series: [*/}
      {/*      {*/}
      {/*        dataKey: "consumptionV",*/}
      {/*        label: gettext("Consumption"),*/}
      {/*        color: (theme.palette as any).chart.electricity,*/}
      {/*        unit: gettext("kWh"),*/}
      {/*      },*/}
      {/*    ],*/}
      {/*  }}*/}
      {/*/>*/}

      {SHOW_AREA_CHART && (
        <>
          <AreaChartBox
            loading={
              loadingThisYear || loadingPreviousYear || loadingTwoYearsAgo
            }
            title={gettext("Consumption (kWh)")}
            chartId={"consumption"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: `consumptionV_${twoYearsAgo}`,
                  label: twoYearsAgo.toString(),
                  color: (theme.palette as any).chart.year3 || "#4472c4",
                  unit: gettext("kWh"),
                },
                {
                  dataKey: `consumptionV_${previousYear}`,
                  label: previousYear.toString(),
                  color: (theme.palette as any).chart.year2,
                  unit: gettext("kWh"),
                },
                {
                  dataKey: `consumptionV_${thisYear}`,
                  label: thisYear.toString(),
                  color: (theme.palette as any).chart.year1,
                  unit: gettext("kWh"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_LINE_CHART && (
        <>
          <LineChartBox
            loading={
              loadingThisYear || loadingPreviousYear || loadingTwoYearsAgo
            }
            title={gettext("Consumption (kWh)")}
            chartId={"consumption-line"}
            data={{
              dataset: chartData,
              series: [
                {
                  dataKey: `consumptionV_${twoYearsAgo}`,
                  label: twoYearsAgo.toString(),
                  color: (theme.palette as any).chart.year3 || "#4472c4",
                  unit: gettext("kWh"),
                },
                {
                  dataKey: `consumptionV_${previousYear}`,
                  label: previousYear.toString(),
                  color: (theme.palette as any).chart.year2,
                  unit: gettext("kWh"),
                },
                {
                  dataKey: `consumptionV_${thisYear}`,
                  label: thisYear.toString(),
                  color: (theme.palette as any).chart.year1,
                  unit: gettext("kWh"),
                },
              ],
            }}
          />
          <br />
        </>
      )}

      {SHOW_BAR_CHART && (
        <>
          <BarChartBox
            loading={
              loadingThisYear || loadingPreviousYear || loadingTwoYearsAgo
            }
            title={gettext("Consumption (kWh)")}
            chartId={"consumption-bar"}
            data={{
              dataset: chartData as any,
              series: [
                {
                  dataKey: `consumptionV_${twoYearsAgo}`,
                  label: twoYearsAgo.toString(),
                  color: (theme.palette as any).chart.year3 || "#4472c4",
                  unit: gettext("kWh"),
                },
                {
                  dataKey: `consumptionV_${previousYear}`,
                  label: previousYear.toString(),
                  color: (theme.palette as any).chart.year2,
                  unit: gettext("kWh"),
                },
                {
                  dataKey: `consumptionV_${thisYear}`,
                  label: thisYear.toString(),
                  color: (theme.palette as any).chart.year1,
                  unit: gettext("kWh"),
                },
              ],
            }}
          />
          <br />
        </>
      )}
      <TableView1
        title={gettext("Consumption")}
        rows={tableData}
        columns={columnsConsumption}
        getRowHeight={() => "auto"}
        hideFooterPagination
        loading={loadingThisYear || loadingPreviousYear || loadingTwoYearsAgo}
        initialState={initialState}
        postToolbar={postToolbar()}
        showGridToolbar
        disableColumnSorting
        disableColumnSelector
        columnGroupingModel={columnGroupingModelConsumption}
        getRowClassName={(params: GridRowClassNameParams) => {
          return params.row.id === "Total" ? "total-row" : "";
        }}
      />

      {energyType === "gas" && (
        <>
          <br />
          <p>
            {gettext("The 2025 and 2026")}{" "}
            <a
              href="https://evida.dk/co2-faktor"
              target="_blank"
              rel="noopener noreferrer"
            >
              {gettext("conversion rates")}
            </a>{" "}
            {gettext(
              "have not yet been released, so we use 2024 for those years until we have data.",
            )}
          </p>
        </>
      )}
    </>
  );
};
